import React, { useContext, useState } from 'react';

import AJAX from 'common/AJAX';
import { CompanyContext } from 'common/containers/CompanyContainer';
import SourceFilter from 'common/subdomain/admin/AdminQueue/SourceFilters/SourceFilter';
import { useIntercomSourceFilter } from 'common/subdomain/admin/AdminQueue/SourceFilters/useIntercomSourceFilter';
import Field from 'common/uiV3/Field';
import Input from 'common/uiV3/Input';
import parseAPIResponse, { isDefaultSuccessResponse } from 'common/util/parseAPIResponse';

import SourceFiltersModal from './SourceFiltersModal';

import type { Integration } from 'common/subdomain/admin/AdminQueue/GettingStarted/constants';

type Props = {
  onClose: () => void;
  integration: Integration;
  enableIntegrationSync: (integration: Integration) => void;
};

const IntercomSourceFiltersModal = ({ onClose, integration, enableIntegrationSync }: Props) => {
  const company = useContext(CompanyContext);
  const { intercom } = company;
  const [selectedIDs, setSelectedIDs] = useState<string[] | null>(
    intercom?.autopilotTeamInboxIDs ?? null,
  );
  const [dayLimit, setDayLimit] = useState<number>(30);
  const { loading, error, options } = useIntercomSourceFilter(selectedIDs);

  const [saveError, setSaveError] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const onSubmit = async () => {
    setSaving(true);
    setSaveError(false);

    const response = await AJAX.post('/api/intercom/setFilterSettings', {
      teamIDs: selectedIDs,
      dayLimit,
    });
    const { error } = parseAPIResponse(response, {
      isSuccessful: isDefaultSuccessResponse,
    });

    if (error) {
      setSaveError(true);
    } else {
      enableIntegrationSync(integration);
      onClose();
    }
    setSaving(false);
  };

  return (
    <SourceFiltersModal
      saving={saving}
      onClose={onClose}
      integration={integration}
      enableIntegrationSync={enableIntegrationSync}
      onSubmit={onSubmit}
      loading={loading}
      error={error}
      disabled={dayLimit > 180}
      sourceFilter={
        <Field.Group className="gap-2">
          <Field>
            <Field.Label>Sync conversation history (days)</Field.Label>
            <Field.Content>
              <Input
                aria-invalid={dayLimit > 180}
                autoFocus
                id="dayLimit"
                onChange={(value: string) => setDayLimit(Number(value))}
                placeholder="Sync limit"
                value={dayLimit}
              />
            </Field.Content>
          </Field>
          <SourceFilter
            options={options}
            value={selectedIDs ?? []}
            filterLabel="Filter by team inbox"
            onChange={setSelectedIDs}
            saveError={saveError}
          />
        </Field.Group>
      }
    />
  );
};

export default IntercomSourceFiltersModal;
